<template>
  <div data-component-name="AppSubscribe">
    <Forms.Wrapper>
      <div class="top">
        <strong class="nowrap">
          {{ $t('Stay_up_to_date') }}
        </strong>
      </div>

      <form data-id="AppSubscribe" autocomplete="off" @submit="submitHandler">
        <AppInput
          v-model="email"
          :error="errors.email"
          :label="$t('Corporate_Email')"
          type="email"
        />

        <Forms.GDPR v-model="GDPR" :error="errors.GDPR" :country-i-s-o />

        <Buttons.Regular :disabled="!meta.valid || isSubmitting">
          {{ $t('Subscribe') }}
          <SvgIcon name="arrow-forward" />
        </Buttons.Regular>
      </form>
    </Forms.Wrapper>

    <transition name="fade">
      <div v-if="isSubmitted" class="overlay">
        <p
          v-html="
            $t(
              isAlreadySubscribed
                ? 'AppSubscribe.already-subscribed'
                : 'AppSubscribe.subscribed',
            )
          "
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import * as yup from 'yup';

// composable
import { useI18n } from '#i18n';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';

// stores
import useUIStore from '~/stores/ui';

// components
import Forms from '~/components/redesign/Forms';
import AppInput from '~/components/redesign/AppInput.vue';
import Buttons from '~/components/redesign/Buttons';

// constants
import { REGEX, API_ENDPOINTS, STATUS_CODES } from '~/constants';

// data
import EMEA_ISO_CODES from '~/data/emea-iso-codes.json';

// utils
import get from 'lodash.get';
import omit from 'lodash.omit';

// exceptions
import { FormValidationError } from '~/exceptions';

const { t } = useI18n();
const {
  public: { api_app: baseURL },
} = useRuntimeConfig();

const commonStore = useCommonStore();
const { ipInfo } = storeToRefs(commonStore);

const uiStore = useUIStore();
const { setLoading } = uiStore;

const { $osano, $pp } = useNuxtApp();

const countryISO = computed(() => get(ipInfo.value, 'iso_code'));

const validationSchema = toTypedSchema(
  yup.object({
    email: yup
      .string()
      .matches(REGEX.EMAIL, t('Validations.Invalid_email_address'))
      .companyEmail()
      .email()
      .required(t('Validations.This_field_is_required')),

    GDPR: yup.boolean().when({
      is: () => EMEA_ISO_CODES.includes(countryISO.value),
      then: schema =>
        schema
          .oneOf([true], t('Validations.This_field_is_required'))
          .required(t('Validations.This_field_is_required')),
      otherwise: schema => schema.notRequired(),
    }),
  }),
);

const {
  errors,
  defineField,
  handleSubmit,
  setErrors,
  resetForm,
  meta,
  isSubmitting,
  setValues,
} = useForm({ validationSchema });

onMounted(() => {
  setValues($pp.getBulk(['email']), false);
});

const [email] = defineField('email');
const [GDPR] = defineField('GDPR');

const isSubmitted = ref(false);
const isAlreadySubscribed = ref(false);

const submitHandler = handleSubmit(async values => {
  try {
    setLoading(true);

    await $fetch(API_ENDPOINTS.SUBSCRIBE, {
      baseURL,
      method: 'POST',
      body: {
        ...omit(values, 'GDPR'),
        country: ipInfo.value?.country,
        consent: $osano.cm.getConsent(),
      },
      credentials: 'include',
    });

    resetForm();
    $pp.update('email', values.email);

    isSubmitted.value = true;
  } catch (error) {
    if (error instanceof FormValidationError) {
      if (error.code === STATUS_CODES.CONFLICT) {
        isSubmitted.value = true;
        isAlreadySubscribed.value = true;
      } else {
        setErrors({ email: t('Validations.real_email') });
      }
    }
  } finally {
    setLoading(false);
  }
});
</script>

<style scoped lang="scss">
@use '_/variables/shadow';
@use '_/mixins/typo';
@use '_/mixins/position';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/fn';

[data-component-name='AppSubscribe'] {
  position: relative;

  [data-component-name='Forms.Wrapper'] {
    .top {
      @include flex.center-sb;

      margin-bottom: 2rem;

      @include media.tablet {
        margin-bottom: 1.5rem;
      }

      @include media.mobile {
        margin-bottom: 1rem;
      }

      strong {
        @include typo.subtitle-1;

        margin-right: 0.5rem;
      }
    }

    form {
      @include flex.start-start;

      flex-direction: column;

      [data-component-name='AppInput'] {
        width: 100%;
        margin-bottom: 1rem;
      }

      p {
        @include typo.caption;

        margin-bottom: 1rem;
      }

      [data-component-name='Buttons.Regular'] {
        align-self: flex-end;

        @include media.mobile {
          width: 100%;
        }
      }
    }
  }

  .overlay {
    padding: 2rem;

    @include position.absolute-full;
    @include flex.center-center;

    background-color: fn.token('surf-cont-primary');
    z-index: 2;

    @include media.tablet {
      padding: 1.5rem;
    }

    @include media.mobile {
      padding: 1.5rem 1rem;
    }

    p {
      @include typo.subtitle-1;

      text-align: center;
    }
  }
}
</style>
